"use client";

import React from "react";
import styles from "./radioButton.module.scss";

interface RadioButtonProps {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  variant?: "purchaseType";
  label?: string;
  showLabel?: boolean;
  name?: string;
  id?: string;
  value?: string;
  className?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  /** Extra `data-*` attributes forwarded to the underlying <input> (e.g. Selenium/test hooks). */
  [dataAttr: `data-${string}`]: string | number | boolean | undefined;
}

const RadioButton: React.FC<RadioButtonProps> = ({
  checked,
  defaultChecked,
  disabled = false,
  size = "medium",
  variant,
  label,
  showLabel = true,
  name,
  id,
  value,
  className,
  onChange,
  ariaLabel,
  ariaLabelledBy,
  ...rest
}) => {
  if (process.env.NODE_ENV !== "production") {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn("RadioButton: no accessible name provided. Supply label, ariaLabel, or ariaLabelledBy.");
    }
  }

  const resolvedAriaLabel = ariaLabelledBy ? undefined : ariaLabel || (!showLabel && label ? label : undefined);

  const classNames = [
    styles.RadioButton,
    styles[size],
    variant ? styles[variant] : "",
    disabled ? styles.disabled : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <label className={classNames}>
      <input
        type="radio"
        className={styles.input}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        name={name}
        id={id}
        value={value}
        onChange={onChange}
        aria-label={resolvedAriaLabel}
        aria-labelledby={ariaLabelledBy}
        {...rest}
      />
      <span className={styles.circle}>
        <span className={styles.dot} />
      </span>
      {showLabel && label && <span className={styles.label}>{label}</span>}
    </label>
  );
};

export default RadioButton;
